import { useState, useEffect } from 'react';
import { api, invalidateCached } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, Key } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function AdminSettings() {
  const { isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    api.get('/content/site_settings').then(r => setSettings(r.data)).catch(() => toast.error('Failed to load'));
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/content/site_settings', settings);
      invalidateCached('/content/site_settings');
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new_password.length < 10) {
      toast.error('Password must be at least 10 characters');
      return;
    }
    setChangingPw(true);
    try {
      await api.put('/admin/password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  if (!settings) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#2D5BE3] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Settings
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Manage site settings and admin credentials</p>
      </div>

      {/* Site Settings */}
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6 mb-6">
        <h2 className="text-lg font-bold text-[#0F172A]">Site Settings</h2>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Site Name</label>
          <Input
            data-testid="settings-site-name"
            value={settings.site_name}
            onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Logo URL</label>
          <Input
            data-testid="settings-logo-url"
            value={settings.logo_url}
            onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
            className="rounded-xl"
          />
        </div>
        {isSuperAdmin() && (
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1">Footer Text</label>
            <Input
              data-testid="settings-footer-text"
              value={settings.footer_text}
              onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
              className="rounded-xl"
            />
          </div>
        )}
        <div className="flex justify-end">
          <Button data-testid="save-settings-btn" onClick={handleSaveSettings} disabled={saving}
            className="bg-[#2D5BE3] hover:bg-[#1E4FA1] text-white rounded-xl px-6">
            <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Key size={20} className="text-[#2D5BE3]" />
          <h2 className="text-lg font-bold text-[#0F172A]">Change Password</h2>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Current Password</label>
          <Input
            data-testid="current-password-input"
            type="password"
            value={passwords.current_password}
            onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">New Password</label>
          <Input
            data-testid="new-password-input"
            type="password"
            value={passwords.new_password}
            onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Confirm New Password</label>
          <Input
            data-testid="confirm-password-input"
            type="password"
            value={passwords.confirm_password}
            onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
            className="rounded-xl"
          />
        </div>
        <div className="flex justify-end">
          <Button data-testid="change-password-btn" onClick={handleChangePassword} disabled={changingPw}
            className="bg-[#2D5BE3] hover:bg-[#1E4FA1] text-white rounded-xl px-6">
            <Key size={16} className="mr-2" /> {changingPw ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </div>
    </div>
  );
}
